import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import Lottie, { type LottieRef, type LottieRefCurrentProps } from 'lottie-react';
import { forwardRef, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  CartApprovalDisplay,
  InstacartShopButton,
  LocationSelector,
  PopoverStyled,
  TooltipStyled,
} from 'src/components';
import { useGetCartQuery } from 'src/hooks';
import type { CartApprovalStatus } from 'src/types';

import cartAnimation from 'src/images/lotties/continue-cart.json';

type Props = {
  handleContinue?: (skipApproval: boolean) => void;
  handleApproveOrReject?: (
    response: 'approve' | 'reject',
    rejectReason?: string,
  ) => void;
  totalPrice: string;
  totalQty: number;
  forGetHeader?: boolean;
  forReviewFooter: boolean;
  isLoading: boolean;
  page?: 'get' | 'embed' | 'review';
  requestedByEmail: string;
  orgApprovalStatus?: CartApprovalStatus;
  whitePendingStatus?: boolean;
  cartId: string;
  cartVendor?: string;
  cartAp?: string;
  cartLocale?: string;
};

/**
 * TODO - this component is a mess. Needs to be refactored badly.
 * 1. Container component
 * 2. Components for different pages, etc
 */
export const ContinueTotalBlock = forwardRef<LottieRefCurrentProps, Props>(
  (
    {
      handleContinue,
      handleApproveOrReject,
      totalPrice,
      totalQty,
      forGetHeader = false,
      forReviewFooter,
      isLoading,
      page = 'get',
      requestedByEmail,
      orgApprovalStatus,
      whitePendingStatus = false,
      cartId,
      cartVendor,
      cartAp,
      cartLocale,
    },
    ref,
  ) => {
    const [showLocationSelector, setShowLocationSelector] = useState(false);
    const [open, setOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const showInstacartCta =
      cartVendor === 'instacart' && searchParams.has('instacart-demo');

    const cartQuery = useGetCartQuery({ cartId });

    const anchorEl = useRef<HTMLButtonElement>(null);

    return (
      <div
        className={`relative flex w-full flex-col items-start justify-center gap-2 smMed:gap-6 sm:mr-[88px] md:mr-[100px] lg:ml-auto ${
          forReviewFooter ? 'ml-auto md:w-fit' : '-mb-[9px] lg:w-fit'
        } ${
          page === 'review'
            ? 'flex-wrap xs:items-start xs:justify-start smMed:flex-row smMed:items-center sm:flex-nowrap sm:justify-end'
            : 'smMed:flex-row smMed:items-end smMed:justify-end'
        }`}
      >
        <div
          className={`z-[1] inline-flex w-fit items-center justify-start gap-2 whitespace-nowrap rounded-full pr-0.5 smMed:w-min ${
            forReviewFooter ? 'bg-white' : 'bg-primary-dark'
          } ${
            // Center the total against the h-12 continue button while the row
            // bottom-aligns (the button may have the location selector above it).
            page !== 'review' ? 'smMed:mb-[11px]' : ''
          }`}
        >
          <span
            className={`text-200 ${forReviewFooter ? '' : 'text-offWhite'}`}
          >
            {t('common.cart_total')}
          </span>
          {isLoading ? (
            <div
              className={`${forReviewFooter ? 'skeleton-light' : 'skeleton-dark'} h-[26px] w-[110px]`}
            ></div>
          ) : (
            <span
              className={`heading-200 ${forReviewFooter ? '' : 'text-offWhite'}`}
            >
              {totalPrice}
            </span>
          )}
        </div>
        {isLoading ? (
          <div
            className={`${forReviewFooter ? 'skeleton-light' : 'skeleton-dark'} h-12 w-full smMed:w-[216px] sm:max-w-[260px]`}
          ></div>
        ) : page === 'review' ? (
          <div className='flex w-full flex-col items-center justify-center gap-2 xs:flex-row smMed:justify-end'>
            <ActionPerformedAlreadyTooltipWrapper
              status={orgApprovalStatus || 'pending'}
              button={'rejected'}
            >
              <button
                className={`button-secondary h-12 disabled:opacity-100 disabled:hover:bg-primary-off ${forReviewFooter ? '' : 'bg-primary-off text-offWhite hover:bg-primary-off/80'} w-full xs:w-[180px] xs:min-w-[180px] xs:max-w-[180px]`}
                onClick={() => setOpen(true)}
                ref={anchorEl}
                disabled={orgApprovalStatus === 'rejected'}
              >
                {orgApprovalStatus === 'rejected' ? (
                  <>
                    Rejected&nbsp;
                    <BlockIcon style={{ fontSize: 24 }} />
                  </>
                ) : (
                  'Reject'
                )}
              </button>
            </ActionPerformedAlreadyTooltipWrapper>
            <PopoverStyled
              id='reject-popover'
              open={open}
              anchorEl={anchorEl.current}
              onClose={() => setOpen(false)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <div className='relative flex w-full min-w-[280px] max-w-[504px] flex-col gap-3 rounded-2xl bg-cloudGray p-6 sm:min-w-[380px] md:min-w-[400px] lg:min-w-[504px]'>
                <div
                  onClick={() => setOpen(false)}
                  className='absolute right-3 top-3 cursor-pointer fill-darkSteel hover:fill-gray-400'
                >
                  <CloseIcon
                    className='transition-all'
                    style={{ fontSize: '24px', fill: 'inherit' }}
                  />
                </div>
                <h5 className='heading-200 text-primary-dark'>Reasons</h5>
                <TextareaAutosize
                  name='cart-note'
                  cols={60}
                  minRows={3}
                  maxLength={1024}
                  defaultValue={''}
                  placeholder={'Explain why this cart is being rejected'}
                  onChange={(e) => setRejectReason(e.target.value)}
                  // disabled={mutation.isPending}
                  autoFocus
                  // onFocus={(e) => {
                  //   const val = e.target.value;
                  //   e.target.value = '';
                  //   e.target.value = val;
                  // }}
                  className='w-full rounded border-none bg-offWhite px-3 py-4 font-secondary font-semibold leading-[24px] text-primary-dark outline-none transition-all selection:bg-primary selection:text-offWhite placeholder:font-normal placeholder:text-[#636E7A] hover:bg-gray-200 focus:ring-2 focus:ring-primary hover:focus:bg-offWhite'
                />

                <div className='inline-flex h-12 w-full flex-nowrap justify-end gap-2'>
                  <button
                    onClick={() => setOpen(false)}
                    className='button-secondary h-12 w-[102px]'
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() =>
                      handleApproveOrReject!('reject', rejectReason)
                    }
                    // disabled={rejectReason.length === 0}
                    className='button-primary h-12'
                  >
                    Send Rejection
                  </button>
                </div>
              </div>
            </PopoverStyled>
            <ActionPerformedAlreadyTooltipWrapper
              status={orgApprovalStatus || 'pending'}
              button={'approved'}
            >
              <button
                className='button-primary h-12 w-full disabled:opacity-100 disabled:hover:bg-primary xs:w-[180px] xs:min-w-[180px] xs:max-w-[180px]'
                onClick={() => handleApproveOrReject!('approve')}
                disabled={orgApprovalStatus === 'approved'}
              >
                {orgApprovalStatus === 'approved' ? (
                  <>
                    Approved&nbsp;
                    <TaskAltIcon style={{ fontSize: 24 }} />
                  </>
                ) : (
                  'Approve'
                )}
              </button>
            </ActionPerformedAlreadyTooltipWrapper>
          </div>
        ) : page === 'get' &&
          (orgApprovalStatus === 'pending' ||
            orgApprovalStatus === 'rejected') ? (
          <CartApprovalDisplay
            cartApprovalDetails={cartQuery.data?.approvalDetails || null}
            cartApprovalStatus={orgApprovalStatus}
            requestedByEmail={requestedByEmail}
            cartId={cartId}
            includeLink={false}
            whitePendingStatus={whitePendingStatus}
            page={page}
            handleContinue={handleContinue}
          />
        ) : (
          <div className='flex w-full flex-col items-end justify-center gap-4'>
            <LocationSelector
              forGetHeader={forGetHeader}
              cartVendor={cartVendor}
              cartLocale={cartLocale}
              cartAp={cartAp}
              showLocationSelector={showLocationSelector}
              setShowLocationSelector={setShowLocationSelector}
            />
            {showInstacartCta ? (
              <InstacartShopButton onClick={() => handleContinue!(false)} />
            ) : (
              <button
                onClick={() => handleContinue!(false)}
                className='button-primary h-12 w-full smMed:max-w-fit sm:max-w-[260px]'
              >
                {t('common.continue_with_items', { count: totalQty })}
              </button>
            )}
          </div>
        )}
        <div
          className={`absolute -right-[76px] -top-8 z-0 w-40 xs:-top-[46px] xs:w-48 smMed:-right-[88px] smMed:-top-[81px] sm:-right-[200px] md:-right-[240px] md:w-60 ${
            forReviewFooter
              ? 'sm:-top-[32px] md:-top-[42px]'
              : showLocationSelector
                ? 'sm:-top-[4px]'
                : 'sm:-top-[32px] md:-top-[53px]'
          }`}
        >
          <Lottie
            lottieRef={ref as LottieRef}
            animationData={cartAnimation}
            style={{ width: '100%', height: 'auto' }}
            autoplay={false}
            loop={false}
          />
        </div>
      </div>
    );
  },
);

const ActionPerformedAlreadyTooltipWrapper = ({
  status,
  button,
  children,
}: {
  status: CartApprovalStatus;
  button: 'approved' | 'rejected';
  children: React.ReactNode;
}) => {
  if (status === button) {
    return (
      <TooltipStyled
        title={`This cart has already been ${status === 'approved' ? 'approved' : 'rejected'}`}
        placement='top'
        arrow={true}
      >
        <div>{children}</div>
      </TooltipStyled>
    );
  }

  return <>{children}</>;
};
